import { useMemo, useState } from "react";
import api, { formatApiErrorDetail, notifyDelete } from "@/lib/api";
import { toast } from "sonner";
import useFeeData from "@/hooks/useFeeData";

const today = () => new Date().toISOString().slice(0, 10);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function suggestPeriod(freq) {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  if (freq === "annual") return `${y}`;
  if (freq === "monthly") return `${y}-${m}`;
  const oneJan = new Date(Date.UTC(y, 0, 1));
  const week = Math.ceil((((d - oneJan) / MS_PER_DAY) + oneJan.getUTCDay() + 1) / 7);
  return `${y}-W${String(week).padStart(2, "0")}`;
}

export function groupPayments(payments) {
  const byMember = {};
  payments.forEach((p) => { (byMember[p.member_id] ||= []).push(p); });
  const totalsByMember = Object.fromEntries(
    Object.entries(byMember).map(([mid, list]) => [mid, list.reduce((s, p) => s + (p.amount || 0), 0)])
  );
  return { byMember, totalsByMember };
}

const emptyForm = { member_id: "", period: "", amount: 0, method: "cash", paid_on: today(), notes: "", send_receipt: true };
const errMsg = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export default function useFees() {
  const { members, payments, overdueMap, load } = useFeeData();
  const [reminding, setReminding] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const openFor = (mem) => {
    setForm({ ...emptyForm, member_id: mem.id, period: suggestPeriod(mem.fee_frequency), amount: mem.fee_amount || 0, paid_on: today() });
    setOpen(true);
  };

  const save = async () => {
    try {
      const { data } = await api.post("/fees", { ...form, amount: Number(form.amount) });
      toast.success(data.receipt_queued ? "Fee payment recorded — receipt emailed to parent" : "Fee payment recorded");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this fee payment (and its ledger entry)?")) return;
    const { data } = await api.delete(`/fees/${id}`);
    notifyDelete(data, "Payment removed");
    load();
  };

  const remind = async (member) => {
    setReminding(member.id);
    try {
      const { data } = await api.post("/reminders/fees", { member_id: member.id });
      toast.success(`Reminder sent to ${data.sent_to}`);
    } catch (e) { toast.error(errMsg(e)); }
    setReminding(null);
  };

  const resendReceipt = async (id) => {
    try {
      const { data } = await api.post(`/fees/${id}/resend-receipt`);
      toast.success(`Receipt ${data.receipt_no} re-sent to ${data.sent_to}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const { byMember, totalsByMember } = useMemo(() => groupPayments(payments), [payments]);
  const currentMember = members.find((m) => m.id === form.member_id);

  return { members, byMember, totalsByMember, overdueMap, reminding, open, setOpen, form, setForm, currentMember, openFor, save, del, remind, resendReceipt };
}
